<template>
  <div class="bookmark-manage-root">
    <div class="header">
      <span>模板名称：{{ realTemplateName }}</span>
    </div>
    <div class="main-layout">
      <!-- 左侧书签列表 -->
      <div class="sidebar">
        <div class="bookmark-list-header">
          <el-button type="primary" size="small" @click="openAddDialog">新增书签</el-button>
        </div>
        <el-table
          :data="tableData"
          border
          v-loading="loading"
          highlight-current-row
          @current-change="handleSelectBookmark"
          :row-class-name="rowClassName"
          class="bookmark-table"
        >
          <el-table-column prop="bookmarkKey" label="书签key" min-width="160" />
          <el-table-column prop="isEditable" label="可维护" min-width="50">
            <template slot-scope="scope">
              <span>{{ scope.row.isEditable === 1 ? '是' : '否' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="isLlmGenerateIndependent" label="单独生成" min-width="80">
            <template slot-scope="scope">
              <span>{{ scope.row.isLlmGenerateIndependent === 1 ? '是' : '否' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="140">
            <template slot-scope="scope">
              <el-button type="text" size="mini" @click.stop="openEditDialog(scope.row)">编辑</el-button>
              <el-button type="text" size="mini" style="color:#f56c6c" @click.stop="handleDelete(scope.row)">删除</el-button>
              <el-button type="text" size="mini" icon="el-icon-arrow-up" :disabled="scope.$index === 0" @click.stop="moveBookmark(scope.row, 'up')"></el-button>
              <el-button type="text" size="mini" icon="el-icon-arrow-down" :disabled="scope.$index === tableData.length - 1" @click.stop="moveBookmark(scope.row, 'down')"></el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <!-- 右侧OnlyOffice文书模板编辑区 -->
      <div class="content-area">
        <OnlyOffice
          v-if="templateFileUrl"
          :file-url="templateFileUrl"
          :title="realTemplateName"
          :mode="'edit'"
          :width="'65vw'"
          :height="'90vh'"
          :editor-config="{
              customization: {
                autosave: false,
                forcesave: true
              },
              callbackUrl: getOnlyofficeCallbackUrl()
            }"
          ref="onlyoffice"
        />
        <div v-else class="loading-area">模板文件加载中...</div>
      </div>
    </div>
    <!-- 书签新增/编辑弹窗 -->
    <BookmarkDialog
      :visible.sync="dialogVisible"
      :mode="dialogMode"
      :bookmark="editingBookmark"
      @submit="handleDialogSubmit"
      @cancel="dialogVisible = false"
    />
  </div>
</template>

<script>
import request from '@/utils/request'
import OnlyOffice from '@/components/OnlyOffice.vue'
import BookmarkDialog from '@/components/BookmarkDialog.vue'
import { getTemplateBookmarks, addTemplateBookmark, updateTemplateBookmark, deleteTemplateBookmark, moveTemplateBookmark } from '@/api/templateBookmark'
import { getTemplateFileUrl, saveTemplateFile } from '@/api/template'

export default {
  name: 'BookmarkManage',
  components: { OnlyOffice, BookmarkDialog },
  props: {
    templateId: { type: String, required: false },
    templateName: { type: String, required: false }
  },
  data() {
    return {
      tableData: [],
      loading: false,
      selectedBookmark: null,
      realTemplateId: '',
      realTemplateName: '',
      templateFileUrl: '',
      onlyofficeCallbackUrl: '',
      saving: false,
      // 书签弹窗相关
      dialogVisible: false,
      dialogMode: 'add', // add/edit
      editingBookmark: null
    }
  },
  created() {
    this.realTemplateId = this.$route.params.templateId || this.templateId
    this.realTemplateName = this.$route.params.templateName || this.templateName
    console.log('realTemplateId', this.realTemplateId)
    if (this.realTemplateId) {
      this.fetchData()
      this.loadTemplateFile()
    }
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const res = await getTemplateBookmarks(this.realTemplateId)
        if (res.code === 200) {
          this.tableData = res.data.map(item => ({ ...item, saving: false }))
          this.selectedBookmark = this.tableData.length > 0 ? this.tableData[0] : null
        }
      } catch (e) {
        this.$message.error('获取书签列表失败')
      } finally {
        this.loading = false
      }
    },
    async loadTemplateFile() {
      try {
        const res = await getTemplateFileUrl(this.realTemplateId)
        console.log('templateFileUrl', res)
        if (res.code === 200 && res.data) {
          this.templateFileUrl = res.data
        }
      } catch (e) {
        console.log('loadTemplateFile error', e)
        this.templateFileUrl = ''
      }
    },
    getOnlyofficeCallbackUrl() {
      // 拼接callbackUrl，带上文书id和token
      const token = localStorage.getItem('token') || ''
      // 读取环境变量，未配置则用默认值
      const base = process.env.VUE_APP_ONLYOFFICE_CALLBACK
      return `${base}?templateId=${this.realTemplateId}&token=${encodeURIComponent(token)}`
    },
    openAddDialog() {
      this.dialogMode = 'add'
      this.editingBookmark = null
      this.dialogVisible = true
    },
    openEditDialog(row) {
      this.dialogMode = 'edit'
      this.editingBookmark = { ...row }
      this.dialogVisible = true
    },
    async handleDialogSubmit(bookmark) {
      try {
        if (this.dialogMode === 'add') {
          await addTemplateBookmark({ ...bookmark, templateId: this.realTemplateId })
          this.$message.success('新增成功')
        } else {
          await updateTemplateBookmark(bookmark)
          this.$message.success('修改成功')
        }
        this.dialogVisible = false
        this.fetchData()
      } catch (e) {
        this.$message.error('操作失败')
      }
    },
    handleSelectBookmark(row) {
      this.selectedBookmark = row
    },
    rowClassName({ row }) {
      return this.selectedBookmark && row.bookmarkId === this.selectedBookmark.bookmarkId ? 'current-row' : ''
    },
    async handleDelete(row) {
      this.$confirm('确定要删除该书签吗？', '提示', { type: 'warning' })
        .then(async () => {
          await deleteTemplateBookmark(row.bookmarkId)
          this.$message.success('删除成功')
          this.fetchData()
        })
    },
    async moveBookmark(row, direction) {
      try {
        await moveTemplateBookmark({ bookmarkId: row.bookmarkId, direction })
        this.fetchData()
      } catch (e) {
        this.$message.error('调整顺序失败')
      }
    },
  }
}
</script>

<style scoped>
.bookmark-manage-root {
  background: #f5f7fa;
  min-height: 100vh;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.header {
  font-size: 20px;
  font-weight: bold;
  margin: 0;
  padding: 24px 32px 12px 32px;
  color: #222;
  background: #fff;
  border-bottom: 1px solid #e0e6ed;
  letter-spacing: 1px;
}
.main-layout {
  flex: 1 1 0;
  display: flex;
  flex-direction: row;
  height: calc(100vh - 57px);
  min-height: 0;
  background: #f5f7fa;
}
.sidebar {
  width: 550px;
  background: #fff;
  border-right: 1px solid #e0e6ed;
  display: flex;
  flex-direction: column;
  padding: 24px 12px 12px 12px;
  height: 100%;
  box-sizing: border-box;
}
.bookmark-list-header {
  margin-bottom: 12px;
  display: flex;
  justify-content: flex-end;
}
.bookmark-table {
  flex: 1 1 0;
  min-height: 0;
  height: 100%;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  overflow-y: auto;
}
.content-area {
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  background: #f9fafc;
  display: flex;
  align-items: stretch;
  justify-content: center;
  position: relative;
  padding: 24px 24px 24px 24px;
  box-sizing: border-box;
}
.loading-area {
  width: 100%;
  height: 100%;
  color: #888;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}
.current-row {
  background: #f0f9eb !important;
}
.el-table th, .el-table td {
  text-align: center;
}
.el-button--primary {
  background: linear-gradient(90deg, #409EFF 0%, #66b1ff 100%);
  border: none;
}
</style> 